import {
  IconWrapper,
  IStyles,
  blobToDataUri,
  downloadPdfToAndroid,
  isAndroidDownloadAvailable,
  pxToRem,
  pxTovW,
} from '@geneo2-web/shared-ui';
import {
  Box,
  Button,
  MenuItem,
  Menu,
  Typography,
  SxProps,
  CircularProgress,
} from '@mui/material';
import { useAppDispatch } from '../../../../../reduxStore/reduxHooks';
import { setToastInfo } from '../../../../../pages/Home/reducer/homeDashboard.slice';
import { useRef, useState } from 'react';
import { AssessmentContent } from '@protos/learning_management/lms.assessment.common.apis_pb';
import PrintableAssessment from '../../../Common/PrintablePaper/PrintableAssessment';
import { useWhiteLabeledUrl } from 'apps/teacher/src/utils/customHooks';
import { OMR_SHEET_PDF_URL } from 'apps/teacher/src/utils/commonVariables';

interface IOmrDownloadButtonsProps {
  assessmentContent?: AssessmentContent;
  assessmentTitle?: string;
  rootStyles?: SxProps;
}

export const OmrDownloadButtons = (props: IOmrDownloadButtonsProps) => {
  const { assessmentContent, assessmentTitle, rootStyles } = props;

  const dispatch = useAppDispatch();
  const school_logo_url = useWhiteLabeledUrl();
  const printableRef = useRef<any>(null);
  const omrSetInfo = assessmentContent?.questionsSequenceInfo?.omrSetInfo ?? [];
  const [isQuestionSetDownloading, setIsQuestionSetDownloading] =
    useState(false);
  const [isAnswerSetDownloading, setIsAnswerSetDownloading] = useState(false);
  const [isOmrSheetDownloading, setIsOmrSheetDownloading] = useState(false);

  const styles: IStyles = {
    root: {
      width: { xs: '90vw', md: pxTovW(700), lg: pxTovW(552) },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: { xs: pxToRem(10), md: pxTovW(12) },
      marginLeft: { xs: pxToRem(20), md: pxTovW(0) },
      ...rootStyles,
    },
  };

  // -----------------------------------------------------

  const downloadQuestionSet = async (setName: string) => {
    setIsQuestionSetDownloading(true);
    try {
      await printableRef.current?.handlePrint({
        title: assessmentTitle,
        setName,
        showAnswer: false,
      });
    } finally {
      setIsQuestionSetDownloading(false);
    }
  };

  // -----------------------------------------------------

  const downloadAnswerSet = async (setName: string) => {
    setIsAnswerSetDownloading(true);
    try {
      await printableRef.current?.handlePrint({
        title: assessmentTitle,
        setName,
        showAnswer: true,
      });
    } finally {
      setIsAnswerSetDownloading(false);
    }
  };

  // -----------------------------------------------------

  const handleDownloadAllQuestionSets = async () => {
    setIsQuestionSetDownloading(true);
    try {
      for (const omrSet of omrSetInfo) {
        await printableRef.current?.handlePrint({
          title: assessmentTitle,
          setName: omrSet.omrSetName,
          showAnswer: false,
        });
      }
    } finally {
      setIsQuestionSetDownloading(false);
    }
  };

  // -----------------------------------------------------

  const handleDownloadAllAnswerSheets = async () => {
    setIsAnswerSetDownloading(true);
    try {
      for (const omrSet of omrSetInfo) {
        await printableRef.current?.handlePrint({
          title: assessmentTitle,
          setName: omrSet.omrSetName,
          showAnswer: true,
        });
      }
    } finally {
      setIsAnswerSetDownloading(false);
    }
  };

  // -----------------------------------------------------

  const questionSetMenuItems = [
    ...(omrSetInfo.length > 1
      ? [
          {
            name: 'Download All',
            onClick: () => void handleDownloadAllQuestionSets(),
          },
        ]
      : []),
    ...omrSetInfo.map((omrSet: any) => ({
      name: `Download Set ${omrSet.omrSetName}`,
      onClick: () => void downloadQuestionSet(omrSet.omrSetName),
    })),
  ];

  // -----------------------------------------------------

  const answerSetMenuItems = [
    ...(omrSetInfo.length > 1
      ? [
          {
            name: 'Download All',
            onClick: () => void handleDownloadAllAnswerSheets(),
          },
        ]
      : []),
    ...omrSetInfo.map((omrSet: any) => ({
      name: `Download Answer Set ${omrSet.omrSetName}`,
      onClick: () => void downloadAnswerSet(omrSet.omrSetName),
    })),
  ];

  // -----------------------------------------------------

  const handleDownloadOmrSheets = async () => {
    const fileName = 'Geneo_Assessment_OMR_SHEET.pdf';

    setIsOmrSheetDownloading(true);
    try {
      const response = await fetch(OMR_SHEET_PDF_URL);

      if (!response.ok) {
        dispatch(
          setToastInfo({
            label: 'Something went wrong',
            variant: 'error',
            open: true,
          })
        );
        return;
      }

      const blob = await response.blob();

      if (isAndroidDownloadAvailable()) {
        // Native's downloadBlob doesn't fetch URLs — it only base64-decodes
        // whatever string it's handed, so send it the fetched PDF as a
        // self-contained data URI instead of the (unfetchable-by-native)
        // remote URL.
        const dataUri = await blobToDataUri(blob);
        downloadPdfToAndroid(dataUri, fileName);
        return;
      }

      const objectUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch {
      dispatch(
        setToastInfo({
          label: 'Something went wrong',
          variant: 'error',
          open: true,
        })
      );
    } finally {
      setIsOmrSheetDownloading(false);
    }
  };

  // -----------------------------------------------------

  return (
    <Box sx={styles.root}>
      <CustomDownloadButton
        label="All Question Sets"
        borderColor="#007CDC"
        backgroundColor="#F8FCFF"
        menuItems={questionSetMenuItems}
        loading={isQuestionSetDownloading}
      />
      <CustomDownloadButton
        label="All Answer Sheet"
        borderColor="#E8B100"
        backgroundColor="#FFF9E6"
        menuItems={answerSetMenuItems}
        loading={isAnswerSetDownloading}
      />
      <CustomDownloadButton
        label="Download OMR Sheets"
        borderColor="#0AA34F"
        backgroundColor="#ECFFF5"
        onClick={handleDownloadOmrSheets}
        loading={isOmrSheetDownloading}
      />

      <PrintableAssessment
        ref={printableRef}
        assessment={assessmentContent}
        schoolLogo={school_logo_url}
      />
    </Box>
  );
};

// ======================================================

interface DownloadMenuItem {
  name: string;
  onClick: () => void;
}
interface ICustomDownloadButtonProps {
  label: string;
  borderColor: string;
  backgroundColor: string;
  onClick?: () => void;
  menuItems?: DownloadMenuItem[];
  rootStyles?: IStyles;
  loading?: boolean;
}

const CustomDownloadButton = (props: ICustomDownloadButtonProps) => {
  const {
    label,
    borderColor,
    backgroundColor,
    onClick,
    menuItems,
    rootStyles,
    loading = false,
  } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick();
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        disableRipple
        onClick={handleClick}
        disabled={loading}
        sx={{
          width: '60%',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 1,
          border: `1px solid ${borderColor}`,
          borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
          backgroundColor,
          color: '#333333',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: pxToRem(8), md: pxTovW(10) },
          paddingY: { xs: pxToRem(10), md: pxTovW(12) },
          textTransform: 'none',
          boxShadow: 'none',
          transition: '0.3s ease all',
          '&::before': {
            transition: '0.5s all ease',
            position: 'absolute',
            top: 0,
            left: '50%',
            right: '50%',
            bottom: 0,
            opacity: 0,
            content: '""',
            backgroundColor: borderColor,
            zIndex: -1,
          },
          '&:hover': {
            color: '#FFFFFF',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            '&::before': {
              transition: '0.5s all ease',
              left: 0,
              right: 0,
              opacity: 1,
            },
            '& img': {
              filter: 'brightness(0) invert(1)',
            },
            '& .MuiTypography-root': {
              color: '#FFFFFF',
            },
          },
          '&:focus': {
            color: '#FFFFFF',
            backgroundColor: 'transparent',
            '&::before': {
              transition: '0.5s all ease',
              left: 0,
              right: 0,
              opacity: 1,
            },
            '& img': {
              filter: 'brightness(0) invert(1)',
            },
            '& .MuiTypography-root': {
              color: '#FFFFFF',
            },
          },
          '&:active': {
            transform: 'scale(0.9)',
          },
          ...rootStyles,
        }}
      >
        {loading ? (
          <CircularProgress
            size={20}
            sx={{
              color: '#333333',
            }}
          />
        ) : (
          <IconWrapper
            name="download-black"
            size="md"
            parentFolder="icons"
            type="png"
            customSx={{
              width: { xs: pxToRem(18), md: pxTovW(20) },
              height: { xs: pxToRem(18), md: pxTovW(20) },
            }}
          />
        )}
        <Typography variant="h4" fontWeight="medium">
          {loading ? 'Starting Download' : label}
        </Typography>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        {menuItems?.map((item, index) => (
          <MenuItem
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              handleMenuClose();
              item.onClick();
            }}
          >
            <Typography
              variant="smallText"
              fontWeight="regular"
              sx={{
                fontFamily: 'Poppins',
              }}
            >
              {item.name}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// ======================================================
